import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// Public landing page — advisors arrive here from either the Biz Resilience
// app or this License Scan app and pick a one-time pass (1-Month or 1-Year).
// All the money is handled by Stripe / PayPal via external payment links
// configured in Admin → Advisor Plans; this page is a pure marketing surface
// with no backend billing logic. Passes are NOT recurring subscriptions —
// each pass grants access to a fixed number of client scans for a fixed
// window and expires at the end of the window.
export default function PlansLanding() {
    const location = useLocation();
    const [plans, setPlans] = useState([]);
    const [freeScansLabel, setFreeScansLabel] = useState("");
    const [footerNote, setFooterNote] = useState("");
    const [backBiz, setBackBiz] = useState("");
    const [backLicense, setBackLicense] = useState("");
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    // ?from=biz-resilience or ?from=license-scan flags help us tailor micro-copy
    const params = new URLSearchParams(location.search);
    const referrer = params.get("from") || "";
    const [cycle, setCycle] = useState("monthly"); // "monthly" | "annual"

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/plans");
                setPlans(data?.plans || []);
                setFreeScansLabel(data?.free_scans_label || "");
                setFooterNote(data?.footer_note || "");
                setBackBiz(data?.back_to_biz_resilience_url || "");
                setBackLicense(data?.back_to_license_scan_url || "");
            } catch (e) {
                setErr(e?.response?.data?.detail || e?.message || "Failed to load plans.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white">
            {/* HERO */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 md:py-24">
                    <div className="overline mb-6 text-[#7a7a7a] tracking-widest text-xs uppercase" data-testid="plans-overline">
                        Advisor Passes — One-time purchase
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-3xl">
                        Serve unlimited clients with a{" "}
                        <span className="text-[#00A896]">Big-4-level advisory kit</span>
                        {" "}for a fraction of the cost.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#c2c2c2] max-w-2xl leading-relaxed">
                        {freeScansLabel ? (
                            <span data-testid="plans-free-scans-copy">{freeScansLabel} </span>
                        ) : null}
                        Upgrade when you&apos;re ready to run repeatable, independent licensing and
                        resilience assessments for the firms in your book.
                    </p>
                    {referrer && (
                        <p className="mt-4 text-xs text-[#7a7a7a]" data-testid="plans-referrer-note">
                            Referred from {referrer.replace(/-/g, " ")}. The same pass unlocks both tools where included.
                        </p>
                    )}
                </div>
            </section>

            {/* CYCLE TOGGLE */}
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-8 flex justify-center">
                    <div
                        className="inline-flex border border-[#2a2a2a] bg-[#141414] p-1"
                        role="tablist"
                        aria-label="Billing cycle"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={cycle === "monthly"}
                            className={`px-6 py-2 text-sm tracking-wide transition-colors ${
                                cycle === "monthly"
                                    ? "bg-[#00A896] text-black"
                                    : "text-[#c2c2c2] hover:text-white"
                            }`}
                            onClick={() => setCycle("monthly")}
                            data-testid="plans-cycle-monthly"
                        >
                            1-Month Access
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={cycle === "annual"}
                            className={`px-6 py-2 text-sm tracking-wide transition-colors ${
                                cycle === "annual"
                                    ? "bg-[#00A896] text-black"
                                    : "text-[#c2c2c2] hover:text-white"
                            }`}
                            onClick={() => setCycle("annual")}
                            data-testid="plans-cycle-annual"
                        >
                            1-Year Access <span className="ml-2 text-xs opacity-70">best value</span>
                        </button>
                    </div>
                </div>
            </section>

            {/* PLAN CARDS */}
            <section>
                <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
                    {loading && (
                        <div className="text-center text-[#7a7a7a]" data-testid="plans-loading">
                            Loading plans…
                        </div>
                    )}
                    {err && (
                        <div
                            className="mx-auto max-w-md border border-red-800 bg-red-950/40 text-red-200 p-4 text-sm"
                            data-testid="plans-error"
                        >
                            {String(err)}
                        </div>
                    )}
                    {!loading && !err && plans.length === 0 && (
                        <div className="text-center text-[#7a7a7a]" data-testid="plans-empty">
                            No active plans right now. Please check back shortly.
                        </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                        {plans.map((p) => (
                            <PlanCard key={p.slug} plan={p} cycle={cycle} />
                        ))}
                    </div>
                    {footerNote && (
                        <p
                            className="mt-10 text-center text-sm text-[#c2c2c2] max-w-2xl mx-auto leading-relaxed whitespace-pre-line"
                            data-testid="plans-footer-note"
                        >
                            {footerNote}
                        </p>
                    )}
                    <p className="mt-6 text-center text-xs text-[#7a7a7a] max-w-2xl mx-auto">
                        Each pass is a one-time purchase — not a recurring subscription.
                        Payments are processed by Stripe or PayPal on their own secure infrastructure;
                        this app never sees your card details.
                    </p>
                    {(backBiz || backLicense) && (
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                            {backBiz && (
                                <a
                                    href={backBiz}
                                    className="btn-secondary inline-flex items-center gap-2"
                                    data-testid="plans-back-biz"
                                >
                                    ← Back to Biz Resilience Scan
                                </a>
                            )}
                            {backLicense ? (
                                <a
                                    href={backLicense}
                                    className="btn-secondary inline-flex items-center gap-2"
                                    data-testid="plans-back-license"
                                >
                                    ← Back to License Risk Scan overview
                                </a>
                            ) : (
                                <Link
                                    to="/"
                                    className="btn-secondary inline-flex items-center gap-2"
                                    data-testid="plans-back-home"
                                >
                                    ← Back to the License Risk Scan
                                </Link>
                            )}
                        </div>
                    )}
                    {!backBiz && !backLicense && (
                        <div className="mt-8 flex justify-center">
                            <Link
                                to="/"
                                className="btn-secondary inline-flex items-center gap-2"
                                data-testid="plans-back-home"
                            >
                                ← Back to the License Risk Scan
                            </Link>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

function PlanCard({ plan, cycle }) {
    const monthly = Number(plan.monthly_price || 0);
    const annual = Number(plan.annual_price || 0);
    const price = cycle === "annual" ? annual : monthly;
    const stripeUrl =
        plan.stripe_enabled !== false
            ? (cycle === "annual" ? plan.stripe_annual_url : plan.stripe_monthly_url) || ""
            : "";
    const paypalUrl =
        plan.paypal_enabled !== false
            ? (cycle === "annual" ? plan.paypal_annual_url : plan.paypal_monthly_url) || ""
            : "";
    const passLabel = cycle === "annual" ? "1-Year pass" : "1-Month pass";
    const scans = cycle === "annual"
        ? Number(plan.annual_scan_limit || 0)
        : Number(plan.monthly_scan_limit || 0);
    const highlight = !!plan.highlighted;

    return (
        <div
            className={`relative flex flex-col p-8 border transition-colors ${
                highlight
                    ? "border-[#2ec5b6] bg-gradient-to-b from-[#0f1f1c] to-[#0a0a0a]"
                    : "border-[#2a2a2a] bg-[#141414]"
            }`}
            data-testid={`plan-card-${plan.slug}`}
        >
            {highlight && (
                <div
                    className="absolute -top-3 left-8 bg-[#00A896] text-black text-[10px] tracking-widest uppercase px-2 py-1"
                    data-testid={`plan-badge-${plan.slug}`}
                >
                    Most popular
                </div>
            )}
            <h2
                className="text-2xl md:text-3xl font-semibold tracking-tight"
                data-testid={`plan-name-${plan.slug}`}
            >
                {plan.display_name}
            </h2>
            <p className="mt-2 text-sm text-[#c2c2c2] leading-relaxed" data-testid={`plan-desc-${plan.slug}`}>
                {plan.description}
            </p>

            <div className="mt-6 flex items-baseline gap-2">
                <span
                    className="text-4xl md:text-5xl font-bold"
                    data-testid={`plan-price-${plan.slug}`}
                >
                    ${Number(price || 0).toLocaleString("en-US")}
                </span>
                <span className="text-[#7a7a7a] text-sm">one-time</span>
            </div>
            <div
                className="mt-1 text-sm text-[#e2e2e2]"
                data-testid={`plan-pass-summary-${plan.slug}`}
            >
                {passLabel}
                {scans > 0 && (
                    <span className="text-[#2ec5b6]">
                        {" "}· {scans.toLocaleString("en-US")} client scan{scans === 1 ? "" : "s"}
                    </span>
                )}
            </div>
            {cycle === "monthly" && annual > 0 && (
                <div
                    className="mt-2 text-xs text-[#9a9a9a]"
                    data-testid={`plan-annual-subline-${plan.slug}`}
                >
                    or ${annual.toLocaleString("en-US")} for a 1-Year pass
                    {Number(plan.annual_scan_limit || 0) > 0 && (
                        <> ({Number(plan.annual_scan_limit).toLocaleString("en-US")} scans)</>
                    )}
                </div>
            )}

            <ul className="mt-6 space-y-2 flex-1" data-testid={`plan-features-${plan.slug}`}>
                {(plan.features || []).map((f, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[#e2e2e2]"
                        data-testid={`plan-feature-${plan.slug}-${i}`}
                    >
                        <span className="text-[#2ec5b6] mt-0.5">✓</span>
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-8 flex flex-col gap-3">
                {stripeUrl && (
                    <a
                        href={stripeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={
                            highlight
                                ? "btn-primary w-full text-center"
                                : "w-full text-center px-6 py-3 border border-[#00A896] text-[#00A896] hover:bg-[#00A896] hover:text-black transition-colors text-sm tracking-wide"
                        }
                        data-testid={`plan-stripe-${plan.slug}-${cycle}`}
                    >
                        Pay with Stripe
                    </a>
                )}
                {paypalUrl && (
                    <a
                        href={paypalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-center px-6 py-3 border border-[#2a2a2a] text-[#e2e2e2] hover:bg-[#1a1a1a] transition-colors text-sm tracking-wide"
                        data-testid={`plan-paypal-${plan.slug}-${cycle}`}
                    >
                        Pay with PayPal
                    </a>
                )}
                {!stripeUrl && !paypalUrl && (
                    <div
                        className="w-full text-center px-6 py-3 border border-dashed border-[#2a2a2a] text-[#7a7a7a] text-xs tracking-wide"
                        data-testid={`plan-checkout-coming-soon-${plan.slug}-${cycle}`}
                    >
                        Checkout links coming soon — contact us to arrange this plan.
                    </div>
                )}
            </div>
        </div>
    );
}
